import { SORT_ORDER } from 'constants.js'
import { createSelector } from 'reselect'
import { getClusterFilter } from 'revenue/selectors'

import { ALL_STORES_COUNTRY_ID, DEFAULT_SORT_BY, REVENUE_SORT_COLUMNS } from './constants'

const getFieldToSort = sortColumn => {
  switch (sortColumn) {
    case REVENUE_SORT_COLUMNS.DIFFERENCE:
      return 'revenuePercent'
    case REVENUE_SORT_COLUMNS.REVENUE_CURRENT_PLN:
    case REVENUE_SORT_COLUMNS.CONTRIBUTION:
      return 'revenueCurrentPLN'
    case REVENUE_SORT_COLUMNS.CONCEPT:
      return 'concept'
    case REVENUE_SORT_COLUMNS.CLUSTER:
      return 'cluster'
    case REVENUE_SORT_COLUMNS.STORE_NAME:
      return 'storeName'
    case REVENUE_SORT_COLUMNS.STORE_ID:
      return 'storeId'
    default:
      return sortColumn
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getCompareFunction = (sortValue, sortOrder) => {
  const sortColumn = getFieldToSort(sortValue)
  switch (sortValue) {
    case REVENUE_SORT_COLUMNS.EXPOS_TIMESTAMP:
    case REVENUE_SORT_COLUMNS.FAIR:
    case REVENUE_SORT_COLUMNS.RESA_TIMESTAMP:
    case REVENUE_SORT_COLUMNS.STORE_NAME:
    case REVENUE_SORT_COLUMNS.TRAFFIC_TIMESTAMP:
      return (storeA, storeB) => {
        const columnName = sortColumn.replace(/\[(.*?)\]/gi, '')
        const isAscending = sortOrder === SORT_ORDER.ASC
        const storeAValue = (storeA[columnName] || '').toLowerCase()
        const storeBValue = (storeB[columnName] || '').toLowerCase()

        return isAscending ? storeBValue.localeCompare(storeAValue) : storeAValue.localeCompare(storeBValue)
      }
    case REVENUE_SORT_COLUMNS.CHANNEL_STORE_NAME:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC
        const storeAValue = `${storeA.storeChannel} ${storeA.storeName}`.toLowerCase()
        const storeBValue = `${storeB.storeChannel} ${storeB.storeName}`.toLowerCase()

        return isAscending ? storeBValue.localeCompare(storeAValue) : storeAValue.localeCompare(storeBValue)
      }
    case REVENUE_SORT_COLUMNS.CONCEPT:
    case REVENUE_SORT_COLUMNS.CLUSTER:
    case REVENUE_SORT_COLUMNS.DATA_SOURCE:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC
        const storeAValue = storeA[sortColumn].toLowerCase()
        const storeBValue = storeB[sortColumn].toLowerCase()
        if (storeAValue > storeBValue) {
          return isAscending ? -1 : 1
        }

        if (storeAValue < storeBValue) {
          return isAscending ? 1 : -1
        }

        return 0
      }
    case REVENUE_SORT_COLUMNS.AVG_VALUE_CLUB:
    case REVENUE_SORT_COLUMNS.CLUB_REVENUE:
    case REVENUE_SORT_COLUMNS.CONTRIBUTION:
    case REVENUE_SORT_COLUMNS.DIFFERENCE:
    case REVENUE_SORT_COLUMNS.ESIZED_CURRENT:
    case REVENUE_SORT_COLUMNS.ESIZEME_SHARE_CURRENT:
    case REVENUE_SORT_COLUMNS.MARGIN:
    case REVENUE_SORT_COLUMNS.MARGIN_VALUE:
    case REVENUE_SORT_COLUMNS.NEW_CARDS:
    case REVENUE_SORT_COLUMNS.ORDER_VALUE:
    case REVENUE_SORT_COLUMNS.ORDERS:
    case REVENUE_SORT_COLUMNS.ORDERS_SHARE:
    case REVENUE_SORT_COLUMNS.ORDERS_VALUE_SHARE:
    case REVENUE_SORT_COLUMNS.ORDERS_VALUE:
    case REVENUE_SORT_COLUMNS.REPLENISHMENT_OTHERS:
    case REVENUE_SORT_COLUMNS.REPLENISHMENT_SHOES:
    case REVENUE_SORT_COLUMNS.RETURNS:
    case REVENUE_SORT_COLUMNS.RETURNS_SHARE:
    case REVENUE_SORT_COLUMNS.REVENUE_CURRENT_PLN:
    case REVENUE_SORT_COLUMNS.STORE_ID:
    case REVENUE_SORT_COLUMNS.REVENUE_PERCENT:
    case REVENUE_SORT_COLUMNS.REVENUE_PERCENT_QTD:
    case REVENUE_SORT_COLUMNS.REVENUE_PERCENT_YTD:
    case REVENUE_SORT_COLUMNS.TEMPERATURE:
    case REVENUE_SORT_COLUMNS.TRAFFIC:
      return (storeA, storeB) => {
        const columnName = sortColumn.replace(/\[(.*?)\]/gi, '')

        return sortOrder === SORT_ORDER.ASC ? storeB[columnName] - storeA[columnName] : storeA[columnName] - storeB[columnName]
      }
    case REVENUE_SORT_COLUMNS.SALE_WITHOUT_RETURNS:
      return (storeA, storeB) =>
        sortOrder === SORT_ORDER.ASC
          ? storeB.revenueCurrent - storeB.returnsValueCurrent - (storeA.revenueCurrent - storeA.returnsValueCurrent)
          : storeA.revenueCurrent - storeA.returnsValueCurrent - (storeB.revenueCurrent - storeB.returnsValueCurrent)
    case REVENUE_SORT_COLUMNS.PERCENT_REVENUE_CLUB:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC
        const storeAValue = (storeA.revenueClubCurrent * 100) / storeA.revenueCurrent
        const storeBValue = (storeB.revenueClubCurrent * 100) / storeB.revenueCurrent

        if ((!storeA && storeB) || storeAValue < storeBValue) {
          return isAscending ? 1 : -1
        }

        if ((storeA && !storeB) || storeAValue > storeBValue) {
          return isAscending ? -1 : 1
        }

        return 0
      }
    case REVENUE_SORT_COLUMNS.L4L:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC
        const storeAValue = !storeA.isL4L
        const storeBValue = !storeB.isL4L

        if ((!storeA && storeB) || storeAValue < storeBValue) {
          return isAscending ? 1 : -1
        }

        if ((storeA && !storeB) || storeAValue > storeBValue) {
          return isAscending ? -1 : 1
        }

        return 0
      }
    case REVENUE_SORT_COLUMNS.SPACE_CHANGE:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC

        if (isAscending) {
          if (storeA.floorSpaceIncreased && !storeB.floorSpaceIncreased) {
            return -1
          }

          if (storeA.floorSpaceDecreased && !storeB.floorSpaceDecreased && !storeB.floorSpaceIncreased) {
            return -1
          }
        } else {
          if (storeA.floorSpaceDecreased && !storeB.floorSpaceDecreased) {
            return -1
          }

          if (storeA.floorSpaceIncreased && !storeB.floorSpaceIncreased && !storeB.floorSpaceDecreased) {
            return -1
          }
        }

        return 0
      }
    case REVENUE_SORT_COLUMNS.EXPRESS:
    case REVENUE_SORT_COLUMNS.ESIZE:
    case REVENUE_SORT_COLUMNS.PROMOTION:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC

        if (storeA[sortColumn] && !storeB[sortColumn]) {
          return isAscending ? 1 : -1
        }

        if (!storeA[sortColumn] && storeB[sortColumn]) {
          return isAscending ? -1 : 1
        }

        return 0
      }
    case REVENUE_SORT_COLUMNS.CHANGED_COMPARED:
      return (storeA, storeB) => {
        const isAscending = sortOrder === SORT_ORDER.ASC

        if ((storeA.lifting && !storeB.lifting) || (storeA.new && !storeB.new)) {
          return isAscending ? 1 : -1
        }

        if ((!storeA.lifting && storeB.lifting) || (!storeA.new && storeB.new)) {
          return isAscending ? -1 : 1
        }

        return 0
      }
    default:
      return () => 0
  }
}

export const getStoreList = createSelector(
  (state, props) => {
    if (props.type === 'country') {
      return state.countries.list.find(country => country.countryId === props.countryId)
    }
    return state.stores
  },
  (state, props) => {
    const countryId = props.countryId || ALL_STORES_COUNTRY_ID
    return state.ui.countryStores.countriesSortingInformation[countryId.toString()] || DEFAULT_SORT_BY
  },
  (state, props) => (props.countryId ? getClusterFilter(state, props) : state.generalRevenue.data.filterByCluster),
  (country, sortBy, clusterFilter) => {
    const { list, stores } = country
    const data = stores || list || []
    const sortFunc = getCompareFunction(sortBy.value, sortBy.order)
    const dataForSort = !clusterFilter
      ? data
      : data.filter(store => store.cluster === clusterFilter || store.storeChannel === clusterFilter)

    return dataForSort
      .sort(getCompareFunction(DEFAULT_SORT_BY.value, DEFAULT_SORT_BY.order))
      .map((store, index) => ({
        ...store,
        position: `${index + 1}. `,
      }))
      .sort(sortFunc)
  },
)

export const getStoreById = (state, storeId) =>
  storeId && state.search.stores ? state.search.stores.find(store => store.storeId === storeId) : null
